import { inject, Injectable } from "@angular/core";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { CheckoutDataService } from "@modules/checkout/services/checkout-data.facade";
import { BehaviorSubject, combineLatest, map, Observable } from "rxjs";
import { MarginTradingRemote } from "./margin-trading.remote";
import { Tradable } from "@models/interfaces/tradable";
import { Tradable as NewTradable } from "@modules/+new/core/tradables/domain/entities/tradable";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { TRADABLES_TYPE } from "@helpers/constants";
import { convertFiatToTradable, convertTradableToFiat } from "@helpers/tradable-convert";
import { fixToPrecisionHelper, setPrecisionDecimals } from "@helpers/utils";
import { Fiat, Conversion } from "@models";
import { PriceConversions } from "@modules/checkout/entities/checkout.model";
import { AccountService } from "@modules/account/services/account.service";
import { AssetFragment } from "../../../api/graphql/bff/generated/fragments.service.graphql-gen";

export interface IMTOrderButtonVisibility {
	isVisible: boolean;
	isAnnounced: boolean;
}

@Injectable({
	providedIn: "root"
})
export class MarginTradingFacade {
	private readonly statsigService = inject(StatsigClientService);
	private checkoutDataService = inject(CheckoutDataService);
	private remote = inject(MarginTradingRemote);
	private assetWalletService = inject(TradableWalletListService);
	private readonly account = inject(AccountService);

	public showMarginTradingRedDot$ = new BehaviorSubject<boolean | null>(
		localStorage.getItem("showMarginTradingRedDot")
			? localStorage.getItem("showMarginTradingRedDot") === "true"
			: null
	);

	setShowMarginTradingRedDot(value: boolean): void {
		localStorage.setItem("showMarginTradingRedDot", JSON.stringify(value));
		this.showMarginTradingRedDot$.next(localStorage.getItem("showMarginTradingRedDot") === "true");
	}

	defaultFiat: Fiat = this.account.getDefaultFiat();

	marginTradingEnabled$(): Observable<boolean> {
		return this.statsigService.isFeatureActive(FEAT_GATES.MARGIN_TRADING_ENABLED);
	}

	getAsset(pid: string): Observable<AssetFragment> {
		return this.remote.getAsset({
			input: { pid },
			includeOHLC: false
		});
	}

	showMarginHeaderSwitchBottomSheetLabel$(): Observable<boolean> {
		return this.statsigService.isFeatureActive(FEAT_GATES.MARGIN_TRADING_HEADER_SWITCH);
	}

	marginTradingBuyEnabled$(): Observable<boolean> {
		return this.statsigService.isFeatureActive(FEAT_GATES.MARGIN_TRADING_BUY);
	}

	marginTradingSellEnabled$(): Observable<boolean> {
		return this.statsigService.isFeatureActive(FEAT_GATES.MARGIN_TRADING_SELL);
	}

	marginTradingSellLimitOrderEnabled$(): Observable<boolean> {
		return this.statsigService.isFeatureActive(FEAT_GATES.MARGIN_TRADING_LIMIT_ORDER);
	}

	marginTradingLimitOrderAnnouncement$(): Observable<boolean> {
		return this.statsigService.isFeatureActive(FEAT_GATES.MARGIN_TRADING_LIMIT_ORDER_ANNOUNCEMENT);
	}

	marginTradingOrderButtonVisibilityHelper(flag: boolean): Observable<IMTOrderButtonVisibility> {
		return combineLatest({
			announcement: this.marginTradingLimitOrderAnnouncement$(),
			isSellMtLoEnabled: this.marginTradingSellLimitOrderEnabled$()
		}).pipe(
			map(({ announcement, isSellMtLoEnabled }) => {
				return {
					isVisible: isSellMtLoEnabled,
					isAnnounced: announcement
				};
			})
		);
	}

	//get under
	getStableCoin$(): Observable<Tradable> {
		return this.remote.getStableCoinAssetId().pipe(
			map((pid) => {
				return this.checkoutDataService.getLegacyTradeableByPid(pid);
			})
		);
	}

	//get stable coin wallet
	getStableCoinWallet$(): Observable<TradableWallet[]> {
		return this.getStableCoin$().pipe(
			map((tradable) => this.assetWalletService.getWalletsForTradable(tradable.getId()))
		);
	}

	checkBooleanQueryParams(payload: boolean | string | undefined): boolean {
		if (typeof payload === "undefined") {
			return false;
		}

		if (typeof payload === "string") {
			return payload === "true";
		}

		return payload;
	}

	// convert tradable to fiat amount helper
	convertTradableToFiatAmountHelper(
		amount: number,
		priceConversions: PriceConversions,
		tradable: NewTradable,
		fiat: Fiat
	): number {
		const conversion = new Conversion();
		conversion.setFiatAmount(priceConversions.price.getPrice().getFiat());
		const converted = convertTradableToFiat(conversion, amount, fiat);
		return tradable.type === TRADABLES_TYPE.INDEX ? amount : +setPrecisionDecimals(converted.toString(), 2);
	}

	// convert fiat to tradable amount helper
	convertFiatToTradableAmountHelper(
		amount: number,
		priceConversions: PriceConversions,
		tradable: Tradable,
		fiat: Fiat
	): number {
		const conversion = new Conversion();
		conversion.setFiatAmount(priceConversions.price.getPrice().getFiat());
		const converted = convertFiatToTradable(fiat, amount, conversion);
		return fixToPrecisionHelper(converted, tradable.getPrecisionForTx());
	}
}
